(() => {
  'use strict';

  const states = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => value * value * (3 - (2 * value));
  const mix = (from, to, amount) => from + ((to - from) * amount);

  function isTarget(node) {
    return node instanceof HTMLElement &&
      node.tagName.toLowerCase().startsWith('scroll-highlight-') &&
      node.dataset.enableAnimation === 'true';
  }

  function rgb(value, fallback) {
    const match = String(value || '').match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
    return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : fallback;
  }

  function findScroller(root) {
    let node = root.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 2) return node;
      node = node.parentElement;
    }

    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const style = getComputedStyle(wrapper);
      if (/(auto|scroll|overlay)/.test(style.overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 2) return wrapper;
    }
    return window;
  }

  function stopOriginalEngine(root) {
    if (root.rafId) {
      cancelAnimationFrame(root.rafId);
      root.rafId = null;
    }
    if (typeof root.handleScroll === 'function') window.removeEventListener('scroll', root.handleScroll);
    if (typeof root.handleResize === 'function') window.removeEventListener('resize', root.handleResize);
  }

  function readColors(words) {
    words.forEach((word) => {
      word.classList.remove('active');
      word.style.removeProperty('color');
    });

    const probe = words[0];
    const base = rgb(getComputedStyle(probe).color, [0, 0, 0]);
    probe.classList.add('active');
    const active = rgb(getComputedStyle(probe).color, [255, 255, 255]);
    probe.classList.remove('active');
    return { base, active };
  }

  function teardown(state) {
    if (!state) return;
    if (state.raf) cancelAnimationFrame(state.raf);
    if (state.scroller !== window) state.scroller.removeEventListener('scroll', state.onScroll);
    else window.removeEventListener('scroll', state.onScroll);
    window.removeEventListener('resize', state.onResize);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    states.delete(state.root);
  }

  function bindScroller(state) {
    const next = findScroller(state.root);
    if (next === state.scroller) return;

    if (state.scroller !== window && state.scroller) state.scroller.removeEventListener('scroll', state.onScroll);
    else if (state.scroller === window) window.removeEventListener('scroll', state.onScroll);

    state.scroller = next;
    if (state.scroller !== window) state.scroller.addEventListener('scroll', state.onScroll, { passive: true });
    else window.addEventListener('scroll', state.onScroll, { passive: true });
  }

  function render(state) {
    state.raf = 0;
    const { root, words } = state;
    if (!root.isConnected) {
      teardown(state);
      return;
    }

    const rect = root.getBoundingClientRect();
    let viewportTop = 0;
    let viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (state.scroller !== window && state.scroller.isConnected) {
      const scrollerRect = state.scroller.getBoundingClientRect();
      viewportTop = scrollerRect.top;
      viewportHeight = scrollerRect.height;
    }
    if (!viewportHeight) return;

    const mobile = matchMedia('(max-width: 749px)').matches;
    const relativeTop = rect.top - viewportTop;
    const entryLine = viewportHeight * (mobile ? 0.90 : 0.84);
    const exitLine = viewportHeight * (mobile ? 0.18 : 0.24);
    const travel = Math.max(1, rect.height + entryLine - exitLine);
    const sensitivity = clamp(Number(root.dataset.sensitivity) || 1, 0.5, 2);

    let progress = clamp((entryLine - relativeTop) / travel);
    const response = 0.92 + (sensitivity * 0.08);
    progress = clamp(0.5 + ((progress - 0.5) * response));

    const count = words.length;
    words.forEach((word, index) => {
      const start = Math.max(0, (index - 0.45) / count);
      const end = (index + 1) / count;
      const local = smoothstep(clamp((progress - start) / Math.max(0.0001, end - start)));
      const r = Math.round(mix(state.base[0], state.active[0], local));
      const g = Math.round(mix(state.base[1], state.active[1], local));
      const b = Math.round(mix(state.base[2], state.active[2], local));
      word.classList.remove('active');
      word.style.color = `rgb(${r}, ${g}, ${b})`;
    });
  }

  function requestRender(state) {
    if (!state.raf) state.raf = requestAnimationFrame(() => render(state));
  }

  function enhance(root) {
    if (!isTarget(root) || states.has(root) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const words = Array.from(root.querySelectorAll('[data-word-index]'));
    if (!words.length) {
      requestAnimationFrame(() => enhance(root));
      return;
    }

    stopOriginalEngine(root);
    root.classList.add('fc-scroll-scrubbed');
    const colors = readColors(words);

    const state = {
      root,
      words,
      base: colors.base,
      active: colors.active,
      scroller: null,
      raf: 0,
      resizeObserver: null,
      onScroll: null,
      onResize: null
    };

    state.onScroll = () => requestRender(state);
    state.onResize = () => {
      bindScroller(state);
      requestRender(state);
    };

    states.set(root, state);
    bindScroller(state);
    window.addEventListener('resize', state.onResize, { passive: true });

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => requestRender(state));
      state.resizeObserver.observe(root);
    }

    requestRender(state);
  }

  function scan(scope = document) {
    if (isTarget(scope)) enhance(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[data-enable-animation="true"]').forEach((node) => {
      if (isTarget(node)) enhance(node);
    });
  }

  function boot() {
    scan();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) scan(node);
      }));
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
